import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Timer, BarChart3, Users, Crown, Settings as SettingsIcon,
  Menu, LogOut, Sun, Moon, Monitor, User as UserIcon, Sparkles, Trophy, Target, CalendarDays,
} from "lucide-react";
import TelegramIcon from "./icons/TelegramIcon";
import { SOCIAL_CHANNELS } from "../lib/social";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { usePremiumStatus } from "../hooks/usePremiumStatus";
import { useTheme } from "../context/ThemeContext";
import useUnreadCount from "../hooks/useUnreadCount";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import PremiumReminderController from "./PremiumReminderController";
import FoundersRewardPopup from "./FoundersRewardPopup";
import AchievementUnlockedPopup from "./AchievementUnlockedPopup";
import TierBadge from "./TierBadge";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";
import { ShieldCheck } from "lucide-react";

// Base sidebar order — the same for everyone. Admin link is injected
// conditionally at render time when the caller has `user.is_admin`.
const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/syllabus", label: "Syllabus", icon: BookOpen, testid: "nav-syllabus" },
  { to: "/timer", label: "Study Timer", icon: Timer, testid: "nav-timer" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/calendar", label: "Study Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/mock-tests", label: "Mock Tests", icon: Target, testid: "nav-mock-tests" },
  { to: "/community", label: "Community", icon: Users, testid: "nav-community" },
  // External Telegram channel — reads config from `lib/social.js` so future
  // channels (Discord, WhatsApp, YouTube) can be added by dropping one more
  // entry in that registry rather than editing the sidebar again.
  {
    href: SOCIAL_CHANNELS.telegram.url,
    label: SOCIAL_CHANNELS.telegram.name,
    icon: TelegramIcon,
    external: true,
    testid: "nav-telegram",
  },
  { to: "/ai", label: "Syllox AI", icon: Sparkles, testid: "nav-ai" },
  { to: "/premium", label: "Premium", icon: Crown, testid: "nav-premium" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { effectivePremium } = useAccess();
  const premium = usePremiumStatus();  // unified plan-state — single source of truth
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = useUnreadCount(effectivePremium);

  // Unified plan state from the `usePremiumStatus` hook — Premium ALWAYS
  // wins over Trial (see hook doc). Prevents the historical race where
  // the header briefly flashed "Trial" after a successful payment while
  // /me/access was still resolving.
  const selfTier = premium.tier;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex items-center gap-2 px-6 py-5">
        <Logo className="h-9 w-9" />
        <div>
          <div className="font-heading text-lg font-bold leading-none">Syllox</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Built for Aspirants</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {(user?.is_admin
          ? [
              ...navItems,
              // Admin link is server-verified — showing it here for
              // non-admins would be a UX lie, not a security hole (the
              // backend 403s any request anyway).
              { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" },
            ]
          : navItems
        ).map((item) => {
          const { icon: Icon, label, testid } = item;
          // External links (e.g. Telegram channel) render as plain <a> tags
          // with target=_blank so they open in a new tab and don't hijack
          // the SPA history. Same visual as internal NavLink items — no
          // active state (they're never "current").
          if (item.external) {
            return (
              <a
                key={testid}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testid}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-900"
              >
                <Icon size={18} />
                <span className="flex-1">{label}</span>
              </a>
            );
          }
          const { to } = item;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                    : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-900"
                }`
              }
            >
              <Icon size={18} />
              <span className="flex-1">{label}</span>
              {to === "/community" && unread > 0 && (
                <span data-testid="unread-badge" className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-zinc-200 p-3 dark:border-zinc-800">
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
              isActive ? "bg-zinc-100 dark:bg-zinc-900" : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-900"
            }`
          }
        >
          <SettingsIcon size={18} /> Settings
        </NavLink>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="lg:flex">
        <div className="hidden lg:block lg:fixed lg:inset-y-0">{Sidebar}</div>
        {open && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <div className="absolute inset-y-0 left-0">{Sidebar}</div>
          </div>
        )}

        <div className="flex-1 lg:pl-64">
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-zinc-200 bg-white/70 px-4 py-3 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/60 sm:px-6">
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="grid h-9 w-9 place-items-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 lg:hidden"
            >
              <Menu size={18} />
            </button>
            <div className="hidden lg:block">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Welcome back</div>
              <div className="font-heading text-lg font-semibold" data-testid="welcome-name">{user?.name}</div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="theme-toggle">
                    {(() => {
                      if (mode === "dark") return <Moon size={18} />;
                      if (mode === "light") return <Sun size={18} />;
                      return <Monitor size={18} />;
                    })()}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setMode("light")} data-testid="theme-light"><Sun size={16} className="mr-2"/>Light</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setMode("dark")} data-testid="theme-dark"><Moon size={16} className="mr-2"/>Dark</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setMode("system")} data-testid="theme-system"><Monitor size={16} className="mr-2"/>System</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-zinc-200 px-2 py-1 hover:bg-zinc-100 dark:border-zinc-800 dark:hover:bg-zinc-900">
                    {user?.picture ? (
                      <img src={user.picture} alt="avatar" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                        {user?.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium">{user?.username}</span>
                    <TierBadge tier={selfTier} size="xs" fullLabel testid="header-tier-badge" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <UserIcon size={16} className="mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <SettingsIcon size={16} className="mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut size={16} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="fade-in mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-testid="app-main">
            {children}
          </main>
        </div>
      </div>
      <PremiumReminderController />
      <FoundersRewardPopup />
    </div>
  );
}
